import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { BaseComponent } from '../base/base.component';
import { ApiEnvNames, API_STATUS, ERROR_RESPONSE, FETCH_METHODS, SearchConfig } from '../../model/api.model';
import { OptionsFetchConfig,LAYOUT_TYPE } from '../../model/common.model';
import { DynamicFormArray, FieldSelectedValue, Props } from '../../model/form.model';
import { InformationTableSchema, InformationFormArray, InformationDynamicOptionFields, InformationTabConfig, InformationTitle, LexusAreasTableData, transformToyotaRegionData, transformCertifiableRolesData, transformNonCertifiableRolesData, transformLexusDealersData, LexusCertificationsTableData, InformationTabIds, ToyotaCertificationsTableData, transformToyotaJobsData, transformToyotaDealersData, transformLexusJobsData, transformDealerBillingCoursesData, AseStatusTableData, BrandSelectionFieldName, LexusDealersTableSchema, ToyotaDealersTableSchema, TotalLexusJobRolesTableSchema, TotalToyotaJobRolesTableSchema, LexusCertificationTableSchema, ToyotaCertificationTableSchema, LexusTableSchema, ToyotaTableSchema, rejectionLexusJobs, rejectionToyotaJobs, collisionLexusJobs, collisionToyotaJobs } from '../../model/information.model';
import { CommonService } from '../../services/common.service';
import { CommonUtils } from '../../utils/common.utils';
 
interface TabApiConfig {
  config: SearchConfig;
  envName: string;
  transform?: (response: any) => any[];
}
 
const TabApiConfigs: Record<string, TabApiConfig> = {
  [InformationTabIds.buySellDealers]: {
    config: { method: FETCH_METHODS['Get'], path: 'getBuySellDealer', body: {} },
    envName: ApiEnvNames['JobEnrollment'],
    transform: (response: any) => (response.result || []).map((item: any, index: number) => ({ sno: index + 1, ...item }))
  },
  [InformationTabIds.dealerBillingCourses]: {
    config: { method: FETCH_METHODS['Get'], path: 'billingReportCoursesList' },
    envName: ApiEnvNames['DealerTrainingBilling'],
    transform: transformDealerBillingCoursesData
  }
};
 
@Component({
  selector: 'app-information',
  standalone: true,
  imports: [PageContainerComponent,CommonModule,MatChipsModule,MatButtonModule,MatCheckboxModule,MatRadioModule,FormsModule],
  templateUrl: './information.component.html',
  styleUrls: ['./information.component.scss']
})
export class InformationComponent extends BaseComponent {
  protected data = 'InformationComponent';
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  public dataTableSchema: DataTableSchema = structuredClone(InformationTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(InformationFormArray);
  protected props: Props = {};
  title=InformationTitle;
  InformationDynamicOptionFields: OptionsFetchConfig = InformationDynamicOptionFields;
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  tabConfig = InformationTabConfig;
  layoutType = LAYOUT_TYPE.Tab;
   private readonly loadedTabs: Set<string> = new Set();
  currentTabIndex = 0;
  
  private readonly brandDrivenTabs = new Set<string>([
    InformationTabIds.lexusAreas,
    InformationTabIds.certifiableJobRoles,
    InformationTabIds.totalLexusJobRoles,
    InformationTabIds.lexusDealers,
    InformationTabIds.lexusCertification,
    InformationTabIds.aseTechRejectionJobRoles,
    InformationTabIds.aseCollisionRejectionJobRoles
  ]);

  private readonly alwaysRefreshTabs = new Set<string>([
    InformationTabIds.lexusAreas,
    InformationTabIds.certifiableJobRoles,
    InformationTabIds.totalLexusJobRoles,
    InformationTabIds.lexusDealers,
    InformationTabIds.lexusCertification,
    InformationTabIds.buySellDealers
  ]);

  private getTab(tabId: string) {
    return this.tabConfig.find(tab => tab.tabId === tabId);
  }

  private getTabFilterSchema(tabId: string): DynamicFormArray | undefined {
    return this.getTab(tabId)?.inputs?.['filterSchema'];
  }

  private setTabDataSource(tabId: string, data: any[]): void {
    const tab = this.getTab(tabId);
    if (!tab?.inputs) {
      return;
    }

    tab.inputs['dataInput'] = data;
    const tableSchema = tab.inputs['DataTableSchema'];
    if (tableSchema) {
      tableSchema.dataSource = data;
    }
  }
 
  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.initializeTabData();
  }
 
  private initializeTabData() {
    this.setTabSchema(InformationTabIds.lexusAreas, LexusTableSchema);
    this.setTabDataSource(InformationTabIds.lexusAreas, LexusAreasTableData);
    this.loadedTabs.add(InformationTabIds.lexusAreas);

    this.configureBrandSelectionFilters();
  }

  private configureBrandSelectionFilters(): void {
    this.brandDrivenTabs.forEach((tabId) => {
      this.configureTabFilterHandler(tabId, BrandSelectionFieldName, 'lexus', this.onBrandSelectionChanged);
    });
  }

  private configureTabFilterHandler(tabId: string, fieldName: string, defaultValue: string, handler: (event: FieldSelectedValue) => void): void {
    const filterSchema = this.getTabFilterSchema(tabId);
    if (!filterSchema?.FieldsArray?.length) {
      return;
    }

    filterSchema.onControlValueChanged = handler;
    const filterField = filterSchema.FieldsArray.find(field => field.fieldName === fieldName);
    if (filterField) {
      filterField.selectedValue = filterField.selectedValue || filterField.defaultValue || defaultValue;
    }
  }
 
  onTabChange(tabIndex: number): void {
    this.currentTabIndex = tabIndex;
    const selectedTab = this.tabConfig[tabIndex];
    if (!selectedTab) {
      return;
    }
    const tabId = selectedTab.tabId;

    if (!this.alwaysRefreshTabs.has(tabId) && this.loadedTabs.has(tabId)) {
      return;
    }

    this.loadedTabs.add(tabId);
    this.fetchTabData(tabId);
  }
 
  private fetchTabData(tabId: string): void {
    switch (tabId) {
      case InformationTabIds.lexusAreas:
        this.fetchAreaRegionData(tabId);
        return;
      case InformationTabIds.certifiableJobRoles:
        this.fetchCertifiableJobRolesData(tabId);
        return;
      case InformationTabIds.totalLexusJobRoles:
        this.fetchJobRolesData(tabId);
        return;
      case InformationTabIds.lexusDealers:
        this.fetchDealersData(tabId);
        return;
      case InformationTabIds.lexusCertification:
        this.fetchCertificationData(tabId);
        return;
      case InformationTabIds.aseStatus:
        this.loadStaticTabData(tabId, AseStatusTableData);
        return;
      case InformationTabIds.aseTechRejectionJobRoles:
        this.fetchAseTechRejectionJobRolesData(tabId);
        return;
      case InformationTabIds.aseCollisionRejectionJobRoles:
        this.fetchAseCollisionRejectionJobRolesData(tabId);
        return;
      default: {
        const apiConfig = TabApiConfigs[tabId];
        if (apiConfig) {
          this.fetchApiData(tabId, apiConfig);
        }
      }
    }
  }

  private readonly onBrandSelectionChanged = (event: FieldSelectedValue) => {
    if (event.FieldName !== BrandSelectionFieldName) {
      return;
    }

    const activeTabId = this.tabConfig[this.currentTabIndex]?.tabId;
    if (!activeTabId || !this.brandDrivenTabs.has(activeTabId)) {
      return;
    }

    this.updateTabFilterSelectedValue(activeTabId, event.Value);
    this.fetchTabData(activeTabId);
  };

  private fetchJobRolesData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    this.setTabSchema(tabId, selectedType === 'toyota' ? TotalToyotaJobRolesTableSchema : TotalLexusJobRolesTableSchema);
    const apiConfig: TabApiConfig = {
      config: { method: FETCH_METHODS['Get'], path: 'lexusAndToyotaDealerDropdown', body: {} },
      envName: ApiEnvNames['StaffMaster'],
      transform: selectedType === 'toyota' ? transformToyotaJobsData : transformLexusJobsData
    };
    this.fetchApiData(tabId, apiConfig);
  }

  private fetchDealersData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    this.setTabSchema(tabId, selectedType === 'toyota' ? ToyotaDealersTableSchema : LexusDealersTableSchema);
    const apiConfig: TabApiConfig = {
      config: { method: FETCH_METHODS['Get'], path: 'lexusAndToyotaDealerDropdown', body: {} },
      envName: ApiEnvNames['StaffMaster'],
      transform: selectedType === 'toyota' ? transformToyotaDealersData : transformLexusDealersData
    };
    this.fetchApiData(tabId, apiConfig);
  }

  private fetchCertificationData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    this.setTabSchema(tabId, selectedType === 'toyota' ? ToyotaCertificationTableSchema : LexusCertificationTableSchema);
    this.loadStaticTabData(tabId, selectedType === 'toyota' ? ToyotaCertificationsTableData : LexusCertificationsTableData);
  }

  private fetchAseTechRejectionJobRolesData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    this.loadStaticTabData(tabId, selectedType === 'toyota' ? rejectionToyotaJobs : rejectionLexusJobs);
  }

  private fetchAseCollisionRejectionJobRolesData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    this.loadStaticTabData(tabId, selectedType === 'toyota' ? collisionToyotaJobs : collisionLexusJobs);
  }

  private fetchAreaRegionData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    if (selectedType === 'toyota') {
      this.setTabSchema(tabId, ToyotaTableSchema);
      const apiConfig: TabApiConfig = {
        config: { method: FETCH_METHODS['Get'], path: 'aseDropdownOptions', body: {} },
        envName: ApiEnvNames['AseReport'],
        transform: transformToyotaRegionData
      };
      this.fetchApiData(tabId, apiConfig);
      return;
    }

    this.setTabSchema(tabId, LexusTableSchema);
    this.loadStaticTabData(tabId, LexusAreasTableData);
  }

  private getSelectedBrand(tabId: string): string {
    const filterSchema = this.getTabFilterSchema(tabId);
    const selectedValue = filterSchema?.FieldsArray?.find((field) => field.fieldName === BrandSelectionFieldName)?.selectedValue;
    return String(selectedValue || 'lexus').toLowerCase();
  }

  private updateTabFilterSelectedValue(tabId: string, selectedValue: string): void {
    const filterSchema = this.getTabFilterSchema(tabId);
    const brandField = filterSchema?.FieldsArray?.find((field) => field.fieldName === BrandSelectionFieldName);
    if (brandField) {
      brandField.selectedValue = selectedValue;
    }
  }

  private setTabSchema(tabId: string, schema: DataTableSchema): void {
    const tab = this.getTab(tabId);
    if (tab?.inputs) {
      tab.inputs['DataTableSchema'] = structuredClone(schema);
    }
  }

  private fetchCertifiableJobRolesData(tabId: string): void {
    const selectedType = this.getSelectedBrand(tabId);
    const certifiableRoleApiConfig: TabApiConfig = {
      config: { method: FETCH_METHODS['Get'], path: 'jobSummaryDropdown' },
      envName: ApiEnvNames['JobEnrollment'],
      transform: selectedType === 'toyota' ? transformNonCertifiableRolesData : transformCertifiableRolesData
    };
    this.fetchApiData(tabId, certifiableRoleApiConfig);
  }
 
  private loadStaticTabData(tabId: string, data: any[]): void {
    this.setTabTableLoading(tabId, true);
    this.setTabDataSource(tabId, data);
    this.setTabTableLoading(tabId, false);
  }
 
  private async fetchApiData(tabId: string, apiConfig: TabApiConfig): Promise<void> {
    this.setTabTableLoading(tabId, true);
    const tab = this.getTab(tabId);
    let tableSchema = tab?.inputs?.['DataTableSchema'];
    if (tableSchema && tab?.inputs) {
      tableSchema = {
        ...tableSchema,
        inputFilter: '',
        activeSorts: [],
        dataSource: []
      };
      tab.inputs['DataTableSchema'] = tableSchema;
    }

    try {
      const response: any = await this.service.getDataFromDataSource(apiConfig.config, apiConfig.envName);
      if (response?.status === API_STATUS['Success']) {
        const transformedData = apiConfig.transform ? apiConfig.transform(response) : response.result;
        this.setTabDataSource(tabId, transformedData || []);
        return;
      }
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
    } catch (err: any) {
      console.info(`Error fetching ${tabId} data:`, err);
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
    } finally {
      this.setTabTableLoading(tabId, false);
    }
  }

  private setTabTableLoading(tabId: string, isLoading: boolean): void {
    const tab = this.getTab(tabId);
    const tableSchema = tab?.inputs?.['DataTableSchema'];
    if (tableSchema) {
      tableSchema.isLoading = isLoading;
    }
  }

}